// libs
import React from 'react'
import { createRoot, Root } from 'react-dom/client'
import LegacyButton, { BUTTON_TYPE_CONTAINED } from 'src/components/forms/button/button'
import Icon from 'src/components/forms/icon/icon'
import { CLASS_PREFIX } from 'src/constants/'

// constants / interfaces
import { DEFAULT_SNACKBAR_TIMEOUT } from 'src/constants/timeouts'
import { textResources } from 'src/lang/de'

// classes
import withPortal from 'src/high_order_components/with_portal'

// styles
import './snack_bar.scss'

export interface IProps {
  message?: string
  showButton?: boolean
  showClose?: boolean
  onClose?: (id?: number) => void
  id?: number
  autoCloseAfterMs?: number
  children?: React.ReactNode
}

const cls = `${CLASS_PREFIX}snack-bar`

const SnackBar: React.FC<IProps> = ({
  message,
  showButton,
  showClose,
  onClose,
  id,
  autoCloseAfterMs,
  children
}) => {
  const timerRef = React.useRef<number | null>(null)

  const clearTimer = React.useCallback(() => {
    if (timerRef.current !== null) {
      window.clearTimeout(timerRef.current)
      timerRef.current = null
    }
  }, [])

  const close = React.useCallback(() => {
    onClose?.(id)
  }, [onClose, id])

  const closeModal = React.useCallback(() => {
    clearTimer()
    close()
  }, [clearTimer, close])

  React.useEffect(() => {
    if (!showClose && !showButton) {
      const ms = typeof autoCloseAfterMs === 'number' ? autoCloseAfterMs : DEFAULT_SNACKBAR_TIMEOUT
      timerRef.current = window.setTimeout(close, ms)
    }
    return clearTimer
  }, [showClose, showButton, autoCloseAfterMs, close, clearTimer])

  return (
    <aside className={cls}>
      <div className={`${cls}__container`}>
        <div className={`${cls}__message`}>
          {message}
          {children}
        </div>

        {showButton && (
          <div className={`${cls}__button`}>
            <LegacyButton
              variant={BUTTON_TYPE_CONTAINED}
              label={textResources.shared.understood}
              onClick={closeModal}
            />
          </div>
        )}
      </div>

      {showClose && (
        <div className={`${cls}__close`} onClick={closeModal}>
          <Icon name='thin-x' description={textResources.screenReaderText.close} />
        </div>
      )}
    </aside>
  )
}

type ShowOptions = {
  showButton?: boolean
  showClose?: boolean
  timeoutMs?: number
  id?: number
}

let hostEl: HTMLDivElement | null = null
let root: Root | null = null

function ensureRoot () {
  if (root) return
  hostEl = document.createElement('div')
  hostEl.setAttribute('data-snackbar-root', 'true')
  document.body.appendChild(hostEl)
  root = createRoot(hostEl)
}

function cleanup () {
  if (root) {
    root.unmount()
    root = null
  }
  if (hostEl) {
    hostEl.remove()
    hostEl = null
  }
}

export function showSnackBar (message: string, opts: ShowOptions = {}) {
  ensureRoot()
  if (!root) return

  const { timeoutMs, ...rest } = opts

  const handleClose = () => {
    cleanup()
  }

  root.render(
    <SnackBar
      message={message}
      onClose={handleClose}
      autoCloseAfterMs={timeoutMs}
      {...rest}
    />
  )
}

export function hideSnackBar () {
  cleanup()
}

// default export for backwards compatibility
// TODO:: Remove when the rest of the app is updated
const SnackBarWithPortal = withPortal(SnackBar)
export default SnackBarWithPortal
